import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './lib/ProtectedRoute';
import AdminRoute from './lib/AdminRoute';
import { useAuth } from './lib/AuthContext';
import { ImpersonationProvider, useIdentity } from './lib/ImpersonationContext';
import AppNav from './components/AppNav';
import Toasts, { toastStore } from './vtt/ui/Toasts';
import RollFeed from './components/RollFeed';
import { getWorldMapLive } from './lib/worldMapLive';
import { WorldMapServerTypes } from './vtt/net/sceneWire';

// Lazy-load page components so the initial bundle only includes the login
// flow and nav shell. Each page (and its dependencies) is fetched on first
// navigation. Vite's code-splitting handles the chunk boundaries automatically.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Vtt = lazy(() => import('./pages/Vtt'));
const Sheet = lazy(() => import('./pages/Sheet'));
const Party = lazy(() => import('./pages/Party'));
const Lore = lazy(() => import('./pages/Lore'));
const LevelUp = lazy(() => import('./pages/LevelUp'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminMaps = lazy(() => import('./pages/AdminMaps'));
const MapViewer = lazy(() => import('./pages/MapViewer'));
const MapViewerIndex = lazy(() =>
  import('./pages/MapViewer').then((m) => ({ default: m.MapViewerIndex })),
);
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));
const Convergence = lazy(() => import('./pages/Convergence'));
const UiKit = lazy(() => import('./pages/UiKit'));
const Sessions = lazy(() => import('./pages/Sessions'));
const RecapReader = lazy(() => import('./pages/RecapReader'));

function AppShell() {
  const { user } = useAuth();
  const { impersonating, effectiveUser, stopImpersonating } = useIdentity();
  const navigate = useNavigate();

  // ---- Reveal-on-any-page toast (§6.C of the world-map plan). --------
  // While the user is signed in we hold one WebSocket open so a DM reveal
  // surfaces a toast regardless of which page they're on (dashboard, lore,
  // sheet, etc.). Clicking the toast deep-links to the affected map. The
  // MapViewer subscribes to the same singleton to also refresh its marker
  // list when the event is for the map it's rendering.
  useEffect(() => {
    if (!user) return;
    const live = getWorldMapLive();
    const off = live.on(WorldMapServerTypes.MarkerRevealed, (p) => {
      const target = p.mapSlug ?? p.mapId;
      toastStore.push(
        'info',
        `A new location has been revealed: ${p.label}`,
        {
          action: {
            onClick: () => navigate(`/map/${encodeURIComponent(target)}`),
            label: `View ${p.label} on the map`,
          },
          // Reveals deserve a bit more dwell time than a generic toast —
          // if a player is mid-turn we want them to notice without having
          // to dig through the nav for "what just popped up?".
          dismissMs: 10_000,
        },
      );
    });
    void live.start();
    return () => {
      off();
      // Don't stop() on unmount — the singleton should stay connected as
      // long as the user is signed in. We only tear down on sign-out, and
      // even then the effect just drops the listener; a re-sign-in triggers
      // start() again which is idempotent.
    };
  }, [user, navigate]);

  return (
    <div className="app-shell">
      {impersonating && (
        <div className="impersonation-banner">
          <span>
            Viewing as <strong>{effectiveUser?.email || effectiveUser?.username}</strong>
          </span>
          <button onClick={stopImpersonating}>Return to DM view</button>
        </div>
      )}
      <div className="app-body">
        <AppNav />
        <main className="app-main">
        <Suspense fallback={<p style={{ padding: 'var(--sp-4)', color: 'var(--fg-muted)' }}>Loading…</p>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* Design-system showcase — public, no backend; redesign Phase 1 reference. */}
          <Route path="/ui-kit" element={<UiKit />} />
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/vtt"
            element={<ProtectedRoute><Vtt /></ProtectedRoute>}
          />
          {/*
            Sessions archive: every player recap the caller can read (newest
            first) + the read-aloud reader per ISO date. Both ride the
            caller-scoped lore API, so players only ever see `Player Recap —
            YYYY-MM-DD` notes carrying `players: all`.
          */}
          <Route
            path="/sessions"
            element={<ProtectedRoute><Sessions /></ProtectedRoute>}
          />
          <Route
            path="/sessions/:date"
            element={<ProtectedRoute><RecapReader /></ProtectedRoute>}
          />
          <Route
            path="/sheet"
            element={<ProtectedRoute><Sheet /></ProtectedRoute>}
          />
          <Route
            path="/party"
            element={<ProtectedRoute><Party /></ProtectedRoute>}
          />
          <Route
            path="/lore"
            element={<ProtectedRoute><Lore /></ProtectedRoute>}
          />
          <Route
            path="/levelup"
            element={<ProtectedRoute><LevelUp /></ProtectedRoute>}
          />
          <Route
            path="/convergence"
            element={<ProtectedRoute><Convergence /></ProtectedRoute>}
          />
          <Route
            path="/audit"
            element={<AdminRoute><AuditDashboard /></AdminRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute><Admin /></AdminRoute>}
          />
          <Route
            path="/admin/maps"
            element={<AdminRoute><AdminMaps /></AdminRoute>}
          />
          {/*
            World-map viewer routes. Step 6 of the world-map plan loosened
            these from AdminRoute to ProtectedRoute: the server-side
            MarkerVisibility filter (see WorldMapVisibility.cs) now
            guarantees players only see markers they're cleared for, so any
            authenticated user can reach the viewer. Map records themselves
            are structural campaign data and are public within the
            campaign.
          */}
          <Route
            path="/map"
            element={<ProtectedRoute><MapViewerIndex /></ProtectedRoute>}
          />
          <Route
            path="/map/:mapId"
            element={<ProtectedRoute><MapViewer /></ProtectedRoute>}
          />
        </Routes>
        </Suspense>
        </main>
      </div>
      <Toasts />
      {/*
        Shared roll feed (dev handoff 2026-08-24): docked on every page so the
        DM sees rolls from the battle map / lore / dashboard without opening a
        sheet. Owns the app-wide session-bound socket (lib/sessionLive.ts).
      */}
      {user && <RollFeed />}
    </div>
  );
}

export default function App() {
  return (
    <ImpersonationProvider>
      <AppShell />
    </ImpersonationProvider>
  );
}
